import {useState, useEffect, useRef} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {doc, setDoc, getDoc, collection, query, where, getDocs, limit, updateDoc, arrayUnion, arrayRemove} from "firebase/firestore";
import {db, auth} from "../firebase";
import {ulid} from "ulid";

import WaInput from "@web.awesome.me/webawesome-pro/dist/react/input";
import WaSelect from "@web.awesome.me/webawesome-pro/dist/react/select";
import WaOption from "@web.awesome.me/webawesome-pro/dist/react/option";
import WaButton from "@web.awesome.me/webawesome-pro/dist/react/button";
import WaIcon from "@web.awesome.me/webawesome-pro/dist/react/icon";
import WaTooltip from "@web.awesome.me/webawesome-pro/dist/react/tooltip";
import WaColorPicker from "@web.awesome.me/webawesome-pro/dist/react/color-picker";
import WaBreadcrumb from "@web.awesome.me/webawesome-pro/dist/react/breadcrumb";
import WaBreadcrumbItem from "@web.awesome.me/webawesome-pro/dist/react/breadcrumb-item";
import WaDialog from "@web.awesome.me/webawesome-pro/dist/react/dialog";

import {MainMenu} from "./Menu";
import {X, LayoutGrid} from "lucide-react";
import {showToast} from "../utils/toast";

import IconSelector from "./IconSelector";

const ChildPage = () => {
    const {childId} = useParams();
	const navigate = useNavigate();

	const [nickname, setNickname] = useState("");
	const [birthMonth, setBirthMonth] = useState("");
	const [birthYear, setBirthYear] = useState("");
	const [gender, setGender] = useState("");
	const [guardian, setGuardian] = useState("");
	const [guardianId, setGuardianId] = useState("");
	const [avatarIcon, setAvatarIcon] = useState("");
	const [avatarColor, setAvatarColor] = useState("");
	const [sharedUsers, setSharedUsers] = useState([]);
	const [sharedUserIds, setSharedUserIds] = useState([]);
	const [shareEmail, setShareEmail] = useState("");
	const [sharing, setSharing] = useState(false);
	const [revokeTargetUser, setRevokeTargetUser] = useState(null);
	const [revoking, setRevoking] = useState(false);

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const shareDialogRef = useRef(null);
	const revokeDialogRef = useRef(null);

    const birthYears = Array.from({length: 18}, (_, i) => new Date().getFullYear() - i);

	const setDialogOpen = (dialogRef, isOpen) => {
		if (dialogRef?.current) {
			dialogRef.current.open = isOpen;
		}
	};

	const openShareDialog = () => {
		if (!childId) {
			showToast("Save this child profile before sharing", "warning");
			return;
		}
		if (auth.currentUser?.uid !== guardianId) {
			showToast("Only the owner can share this child", "warning");
			return;
		}
		setDialogOpen(shareDialogRef, true);
	};

	const handleShareWithEmail = async () => {
		const user = auth.currentUser;
		if (!user || !childId) return;

		const normalizedEmail = shareEmail.trim().toLowerCase();
		if (!normalizedEmail) {
			await showToast("Enter an email address", "warning");
			return;
		}

		setSharing(true);
		try {
			const userQuery = query(collection(db, "user"), where("email", "==", normalizedEmail), limit(1));
			const querySnapshot = await getDocs(userQuery);

			if (querySnapshot.empty) {
				await showToast("No user found with that email", "warning");
				return;
			}

			const targetDoc = querySnapshot.docs[0];
			const targetUserId = targetDoc.id;
			const targetData = targetDoc.data();
			const targetName = `${targetData.firstName || ""} ${targetData.lastName || ""}`.trim() || normalizedEmail;

			if (targetUserId === guardianId) {
				await showToast("This user is already the owner", "warning");
				return;
			}

			if (sharedUserIds.includes(targetUserId)) {
				await showToast("This user already has access", "warning");
				return;
			}

			await updateDoc(doc(db, "child", childId), {
				shared_with: arrayUnion(targetUserId),
			});

			setSharedUserIds((prev) => [...prev, targetUserId]);
			setSharedUsers((prev) => [
				...prev,
				{
					id: targetUserId,
					name: targetName,
					email: targetData.email || normalizedEmail,
				},
			]);
			setDialogOpen(shareDialogRef, false);
			setShareEmail("");
			await showToast("Child shared successfully", "success");
		} catch (error) {
			console.error("Error sharing child profile: ", error);
			await showToast("Error sharing child profile", "danger");
		} finally {
			setSharing(false);
		}
	};

	const openRevokeDialog = (targetUser) => {
		if (auth.currentUser?.uid !== guardianId) {
			showToast("Only the owner can revoke access", "warning");
			return;
		}
		setRevokeTargetUser(targetUser);
		setDialogOpen(revokeDialogRef, true);
	};

	const closeRevokeDialog = () => {
		setRevokeTargetUser(null);
		setDialogOpen(revokeDialogRef, false);
	};

	const handleRevokeSharedUser = async (targetUserId) => {
		const user = auth.currentUser;
		if (!user || !childId) return;

		if (user.uid !== guardianId) {
			await showToast("Only the owner can revoke access", "warning");
			return;
		}

		if (!targetUserId || !sharedUserIds.includes(targetUserId)) {
			await showToast("User does not have access", "warning");
			return;
		}

		setRevoking(true);
		try {
			await updateDoc(doc(db, "child", childId), {
				shared_with: arrayRemove(targetUserId),
			});

			setSharedUserIds((prev) => prev.filter((id) => id !== targetUserId));
			setSharedUsers((prev) => prev.filter((sharedUser) => sharedUser.id !== targetUserId));
			closeRevokeDialog();
			await showToast("Access revoked", "success");
		} catch (error) {
			console.error("Error revoking child share access: ", error);
			await showToast("Error revoking access", "danger");
		} finally {
			setRevoking(false);
		}
	};

	useEffect(() => {
		const fetchChildData = async () => {
			if (!childId) {
				setLoading(false);
				return;
			}
			const user = auth.currentUser;
			if (user) {
				const childDoc = await getDoc(doc(db, "child", childId));
				if (
					childDoc.exists() &&
					(childDoc.data().guardian === user.uid ||
						(childDoc.data().shared_with &&
							childDoc.data().shared_with.includes(user.uid)))
				) {
					const data = childDoc.data();
					setNickname(data.nickname || "");
					setBirthMonth(data.birth_month || "");
					setBirthYear(data.birth_year || "");
					setGender(data.gender || "");
					setAvatarIcon(data.avatar_icon || "");
					setAvatarColor(data.avatar_color || "");
					setGuardianId(data.guardian || "");

					const guardianDoc = await getDoc(doc(db, "user", data.guardian));
					if (guardianDoc.exists()) {
						const guardianData = guardianDoc.data();
						setGuardian(guardianData.firstName + " " + guardianData.lastName);
					}

					const sharedWith = Array.isArray(data.shared_with) ? data.shared_with : [];
					setSharedUserIds(sharedWith);
					if (sharedWith.length > 0) {
						const sharedUserDocs = await Promise.all(sharedWith.map((uid) => getDoc(doc(db, "user", uid))));
						const loadedSharedUsers = sharedUserDocs
							.map((sharedDoc, index) => {
								if (!sharedDoc.exists()) return null;
								const userData = sharedDoc.data();
								const name = `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || userData.email || "Unknown User";
								return {
									id: sharedWith[index],
									name,
									email: userData.email || "",
								};
							})
							.filter(Boolean);
						setSharedUsers(loadedSharedUsers);
					} else {
						setSharedUsers([]);
					}
				} else {
					console.log("No such document!");
					// TODO: display error message that the user doesn't have access to this child profile, and redirect back to profile page after a few seconds
				}
			}
			setLoading(false);
		};
		fetchChildData();
	}, [childId]);

	const handleSave = async () => {
		const user = auth.currentUser;
		if (user) {
			setSaving(true);
			const docId = childId || ulid(); // You can generate this ID as needed
            try {
				await setDoc(doc(db, "child", docId), {
					nickname: nickname || "",
					birth_month: birthMonth || "",
					birth_year: birthYear || "",
					gender: gender || "",
					avatar_icon: avatarIcon,
					avatar_color: avatarColor || "",
					guardian: guardianId || user.uid,
					shared_with: sharedUserIds,
				});

				navigate("/profile#children");
				await showToast("Child profile updated successfully", "success");
			} catch (error) {
				console.error("Error saving child profile: ", error);
				await showToast("Error saving child profile", "danger");
				return;
			} finally {
				setSaving(false);
			}

			// history.back();
		}
	};

	return (
		<div className="child-page page">
			<div className="page-meta">
				<div className="elem-group gap-lg justify-between align-center">
					<h1>Child Profile</h1>
					<MainMenu activePage="profile" />
				</div>
				<WaBreadcrumb>
					<WaBreadcrumbItem href="/">
						<LayoutGrid size={24} />
					</WaBreadcrumbItem>
					<WaBreadcrumbItem href="/profile">Profile</WaBreadcrumbItem>
					<WaBreadcrumbItem href={`/child/${childId}`}>Child</WaBreadcrumbItem>
				</WaBreadcrumb>
			</div>
			<div className="page-content">
				<WaInput
					label="Nickname"
					name="nickname"
					placeholder="Johnny"
					size="large"
					type="text"
					// ref={nicknameRef}
					value={nickname}
					onChange={(e) => setNickname(e.target.value)}
				></WaInput>
				<div className="elem-group gap-x">
					<WaSelect
						label="Birth Month"
						name="birth_month"
						placeholder="January"
						size="large"
						type="text"
						value={birthMonth}
						onChange={(e) => setBirthMonth(e.target.value)}
					>
						<WaOption value="Jan">January</WaOption>
						<WaOption value="Feb">February</WaOption>
						<WaOption value="Mar">March</WaOption>
						<WaOption value="Apr">April</WaOption>
						<WaOption value="May">May</WaOption>
						<WaOption value="Jun">June</WaOption>
						<WaOption value="Jul">July</WaOption>
						<WaOption value="Aug">August</WaOption>
						<WaOption value="Sep">September</WaOption>
						<WaOption value="Oct">October</WaOption>
						<WaOption value="Nov">November</WaOption>
						<WaOption value="Dec">December</WaOption>
					</WaSelect>
					<WaSelect
						label="Birth Year"
						name="birth_year"
						size="large"
						value={birthYear}
						onChange={(e) => setBirthYear(e.target.value)}
					>
						{birthYears.map((year) => (
							<WaOption
								key={year}
								value={year}
							>
								{year}
							</WaOption>
						))}
					</WaSelect>
				</div>
				<WaSelect
					label="Gender"
					name="gender"
					size="large"
					value={gender}
					onChange={(e) => setGender(e.target.value)}
				>
					<WaOption value="Male">Male</WaOption>
					<WaOption value="Female">Female</WaOption>
					<WaOption value="Unset">Prefer Not To Say</WaOption>
				</WaSelect>
				<div className="elem-group gap-lg full-width justify-between">
					<WaColorPicker
						label="Avatar Color"
						name="avatar_color"
						hint="Choose a color for your child's avatar"
						value={avatarColor}
						onChange={(e) => setAvatarColor(e.target.value)}
						size="large"
						format="hex"
						round
					></WaColorPicker>
					<div className="avatar-preview">
						<div
							className="avatar avatar-lg"
							style={{backgroundColor: avatarColor}}
						>
							<WaIcon
								family="default"
								name={avatarIcon}
								color={avatarColor}
							/>
						</div>
					</div>
				</div>

				<IconSelector
					avatarColor={avatarColor}
					value={avatarIcon}
					onChange={(icon) => setAvatarIcon(icon)}
				/>

				<label className="large">Permissions</label>
				<div className="table-scroll-wrapper">
					<table className="scroll">
						<thead>
							<tr>
								<th>Name</th>
								<th>Role</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{guardian && guardianId && (
								<tr>
									<td>
										{guardian}
										{auth.currentUser.uid === guardianId && " (You)"}
									</td>
									<td>Owner</td>
									<td>
										<WaButton
											className="btn-gloss btn-round"
											size="small"
											disabled
											id={`revoke-${guardianId}`}
										>
											<X size={24} />
										</WaButton>
										<WaTooltip for={`revoke-${guardianId}`}>Revoke permission</WaTooltip>
									</td>
								</tr>
							)}
							{sharedUsers && sharedUsers.length > 0 ? (
								sharedUsers.map((user, index) => (
									<tr key={index}>
										<td>
											{user.name}
											{auth.currentUser.uid === user.id && " (You)"}
										</td>
										<td>Viewer</td>
										<td>
											<WaButton
												className="btn-gloss btn-round"
												size="small"
												id={`revoke-${index}`}
												onClick={() => openRevokeDialog(user)}
												disabled={auth.currentUser.uid !== guardianId}
											>
												<X size={24} />
											</WaButton>
											<WaTooltip for={`revoke-${index}`}>{auth.currentUser.uid === guardianId ? "Revoke permission" : "Only owner can revoke"}</WaTooltip>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan="3">Not shared with anyone</td>
								</tr>
							)}
						</tbody>
						<tfoot>
							<tr>
								<td colSpan="3">
									<WaButton
										className="btn-outline"
										size="small"
										pill
										onClick={openShareDialog}
									>
										Share with someone
									</WaButton>
								</td>
							</tr>
						</tfoot>
					</table>
				</div>

				<WaDialog
					ref={shareDialogRef}
					label="Share Child Profile"
				>
					<div className="column gap-md">
						<WaInput
							autofocus
							label="User Email"
							name="share_email"
							type="email"
							placeholder="user@example.com"
							value={shareEmail}
							onChange={(e) => setShareEmail(e.target.value)}
						></WaInput>
						<div className="elem-group gap-sm">
							<WaButton
								className="btn-gloss"
								data-dialog="close"
								slot="footer"
							>
								Cancel
							</WaButton>
							<WaButton
								className="btn-accent"
								onClick={handleShareWithEmail}
								disabled={sharing}
								slot="footer"
							>
								{sharing ? "Sharing..." : "Share"}
							</WaButton>
						</div>
					</div>
				</WaDialog>

				<WaDialog
					ref={revokeDialogRef}
					label="Revoke Access"
				>
					<div className="column gap-md">
						<p>
							Are you sure you want to revoke access for <strong>{revokeTargetUser?.name || "this user"}</strong>?
						</p>
						<div className="elem-group gap-sm">
							<WaButton
								className="btn-gloss"
								onClick={closeRevokeDialog}
								disabled={revoking}
								slot="footer"
							>
								Cancel
							</WaButton>
							<WaButton
								className="btn-accent"
								onClick={() => handleRevokeSharedUser(revokeTargetUser?.id)}
								disabled={revoking || !revokeTargetUser?.id}
								slot="footer"
							>
								{revoking ? "Revoking..." : "Confirm"}
							</WaButton>
						</div>
					</div>
				</WaDialog>

				<div className="elem-group gap-sm">
					<WaButton
						size="large"
						onClick={handleSave}
						className="btn-accent"
						pill
						disabled={saving}
					>
						{saving ? "Saving..." : "Save"}
					</WaButton>
					<WaButton
						className="btn-gloss"
						size="large"
						onClick={() => navigate("/profile#children")}
						pill
					>
						Cancel
					</WaButton>
				</div>
				<hr className="divider" />
				<div className="elem-group gap-sm">
					<WaButton
						className="btn-outline"
						size="small"
						onClick={() => alert("Export functionality not implemented yet")}
						pill
						disabled
					>
						Export Child Data
					</WaButton>
					<WaButton
						className="btn-outline"
						size="small"
						onClick={() => alert("Delete functionality not implemented yet")}
						pill
						disabled
					>
						Delete Child
					</WaButton>
				</div>
			</div>
		</div>
	);
};

export default ChildPage;
